import React, {useState, useRef, useEffect} from 'react';
import {Form, Formik} from 'formik';
import {Trans} from 'react-i18next';
import {history} from '../../../../utils/redux/store';
import _get from 'lodash/get';
import {toast} from 'react-toastify';
import Header from '../../components/header';
import ButtonWithRestoreGroup from '../../../../components/formik/ButtonWithRestoreGroup';
import {useSelector} from 'react-redux';
import {
  getTemplateDefaultConfig,
  getTemplateLayout,
  getTemplate,
  updateTemplate,
  handleImage,
  getBrandId,
  treatmentTypes,
  combineTreatments,
  handleGenericTemplateExport,
} from '../../../../utils/skincareUtils';
import smbUtils from '../../../../utils/smbUtils';
import Loader from '../../../../components/spinner/Loader';
import PreviewModule from '../../components/previewmodule';
import _isEmpty from 'lodash/isEmpty';
import Transform from '../../../../components/i18n/transform';
import PublishToOnlineModal from '../components/PublishToOnlineModal';
import {privilegeUtils} from '../../../../utils/privilegeUtils';
import langUtils from '../../../../utils/langUtils';
import {
  deviceOptions,
  getAvailableDevices,
  getUiConfigDevice,
  setUiConfigDevice,
} from '../config/utils/optionsUtils';
import useSolutionType from '../../../../components/hook/useSolutionType';
import {useQuery} from '../../../../components/hook/useQuery';
import useSkincare from '../../../../components/hook/useSkincare';
import langCode from '../../../../utils/langUtils';
import skincareProDao from '../../../../dao/skincareProDao';
import ChooseUITemplate from '../components/ChooseUITemplate';
import GenericLayoutSettings from './components/GenericLayoutSettings';
import CustomizedLayoutSettings from './components/CustomizedLayoutSettings';
import AccessOptionsHintModal from '../components/AccessOptionsHintModal';
import {isFunctionBusyError} from '../../../../utils/errorUtils';
import ElevatedButton from '../../../../components/formik/ElevatedButton';
import ConfigurationModal from '../components/ConfigurationModal';
import OverLayLoader from '../../../../components/spinner/OverLayLoader';

export default function SimulationReport() {
  const auth = useSelector(state => state.components.auth.accountSummary);
  const {isSimulationPremium} = useSolutionType(auth);
  const {isV1, isVersionAbove2AndHd} = useSkincare(auth);
  const {token, customerId, brandInfo} = smbUtils.customerInfo(auth);
  const formRef = useRef();
  const isTechnicalSupport = privilegeUtils.isTechnicalSupport(auth);

  const [isLoaded, setIsLoaded] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [initValue, setInitValue] = useState({});
  const [commonData, setCommonData] = useState({});
  const [templateId, setTemplateId] = useState('');
  const [generalTempInfo, setGeneralTempInfo] = useState({});
  const [customTempDataset, setCustomTempDataset] = useState([]);
  const [selectedTempInfo, setSelectedTempInfo] = useState({});
  const [device, setDevice] = useState(
    getUiConfigDevice(deviceOptions, customerId)
  );
  const [language, setLanguage] = useState(langUtils.getInitialLang());

  const [publishStatus, setPublishStatus] = useState(false);
  const [isShowPublishModal, setIsShowPublishModal] = useState(false);
  const [isShowAccessOptionsModal, setIsShowAccessOptionsModal] = useState(
    false
  );
  const [modalState, setModalState] = useState({
    isShow: false,
    type: null,
    showFooter: false,
  });

  const [treatment, setTreatment] = useState(treatmentTypes[0]);
  const [treatments, setTreatments] = useState([]);
  const [
    customSimulationTreatmentData,
    setCustomSimulationTreatmentData,
  ] = useState({});
  const templateType = 'SmbSimulationResultPage';
  const [tab, setTab] = useState(templateType);

  const {
    data: templateUrlData,
    isLoading: isLoadingByTemplateUrl,
    refetch: tempPreviewUrlRefetch,
  } = useQuery({
    queryFunc: params => {
      if (!_get(params, 'rvId')) {
        return;
      }
      return _get(params, 'isDefault')
        ? getTemplateLayout(
            token,
            customerId,
            templateType,
            _get(brandInfo, 'templateVersion')
          )
        : getTemplate(token, customerId, _get(params, 'rvId'));
    },
    initData: {
      indexPage: '',
      getPhotoPage: '',
      indexFileUrl: '',
      getPhotoFileUrl: '',
    },
    isInitEnabled: false,
  });

  useEffect(() => {
    if (isV1) {
      history.push('/skincare-pro/ui-configuration/skin-result-page');
    }
    if (smbUtils.isCnSite(auth)) {
      history.push('/skincare-pro/ui-configuration/overview');
    }
    fetchData();
  }, []);

  const updateCustomLang = availableLangs => {
    const availableLangList = langCode.getConsoleStringLangList();
    const matchedLang = availableLangList.find(lang =>
      availableLangs.includes(lang.value)
    );
    setLanguage(matchedLang || availableLangList[0]);
  };

  const updateCustomDevice = availableDevices => {
    setDevice(
      getUiConfigDevice(getAvailableDevices(availableDevices), customerId)
    );
  };

  const fetchData = async () => {
    setIsLoaded(true);
    try {
      const promises = [
        skincareProDao.listUIConfigTemplates({template_type: templateType}),
      ];

      if (isVersionAbove2AndHd) {
        promises.push(
          skincareProDao.getCustomerSetting({
            type: 'ScpAppSettings',
          })
        );
      }

      const results = await Promise.all(
        promises.map(promise => promise.catch(() => ({})))
      );

      const templateLists = results[0];
      const appSettingsConfig = isVersionAbove2AndHd ? results[1] : null;

      if (isVersionAbove2AndHd && appSettingsConfig) {
        const customTreatmentName = _get(
          appSettingsConfig,
          'editing_revision.data.custom_simulation_treatment_name',
          {}
        );
        setCustomSimulationTreatmentData(customTreatmentName);
      }

      const templates = _get(templateLists, 'templates', []);
      setCustomTempDataset(templates.filter(template => !template.is_default));

      const defaultItem = templates.find(t => t.is_default);
      if (!defaultItem) {
        toast.error(
          <Trans i18nKey="toast.error.loading.data.failed">
            Failed to load data.
          </Trans>
        );
        return;
      }
      setGeneralTempInfo(defaultItem);
      //For general template form
      await getTemplate(
        token,
        customerId,
        _get(defaultItem, 'template_revision_id')
      ).then(res => {
        if (res && res.configJson) {
          const parsedConfig = JSON.parse(res.configJson);

          // check if simulationGetPhoto page exists
          const pages = _get(parsedConfig, 'page', []);
          const hasSimulationGetPhoto = pages.some(
            p => p.pageType === 'simulationGetPhoto'
          );

          let finalPages = [];
          if (!hasSimulationGetPhoto && pages.length > 0) {
            const existingPage =
              pages.find(p => p.pageType === 'simulationResult') || pages[0];

            finalPages = [
              existingPage,
              {
                pageType: 'simulationGetPhoto',
                templateType: _get(existingPage, 'templateType', 'skincare'),
                is_default: _get(existingPage, 'is_default', true),
                template_id: _get(existingPage, 'template_id', ''),
                template_revision_id: _get(
                  existingPage,
                  'template_revision_id',
                  null
                ),
                accessOptions: {
                  showQRCode: true,
                  showEmail: true,
                  showPrint: true,
                },
              },
            ];
          } else {
            finalPages = pages;
          }

          setInitValue({
            page: finalPages,
            is_default: _get(res, 'is_default', true),
            template_id: _get(res, 'templateId', ''),
            template_revision_id: _get(
              defaultItem,
              'template_revision_id',
              null
            ),
          });
        }
        setTemplateId(_get(res, 'templateId', ''));
      });

      //For general template post massage
      await getBrandId(token, customerId).then(async res => {
        if (res && res.commonSettingRvId) {
          await getTemplate(
            token,
            customerId,
            _get(res, 'commonSettingRvId'),
            'CommonSetting'
          ).then(res => {
            if (res && res.configJson) {
              setCommonData(JSON.parse(res.configJson));
            }
          });
        }
        if (res && res.facialTreatmentRvId) {
          getTemplate(token, customerId, _get(res, 'facialTreatmentRvId')).then(
            res => {
              if (res && res.metaDataJson) {
                const facialZones = JSON.parse(_get(res, 'metaDataJson', []))
                  .facialZones;
                const treatmentOptions = JSON.parse(
                  _get(res, 'metaDataJson', '')
                ).treatmentOptions;
                if (facialZones.length > 0) {
                  setTreatments(
                    combineTreatments(
                      treatmentOptions,
                      isSimulationPremium,
                      facialZones
                    )
                  );
                }
              }
            }
          );
        }
      });

      const selectedItem = templates.find(t => t.selected);
      if (!selectedItem) {
        toast.error(
          <Trans i18nKey="toast.error.loading.data.failed">
            Failed to load data.
          </Trans>
        );
        return;
      }

      if (selectedItem.is_default) {
        setSelectedTempInfo({
          ...selectedItem,
          device_type: deviceOptions.map(opt => opt.value),
          langs: langCode.getConsoleStringLangList().map(opt => opt.value),
        });
      } else {
        setSelectedTempInfo(selectedItem);
        updateCustomLang(_get(selectedItem, 'langs', []));
        updateCustomDevice(_get(selectedItem, 'device_type', []));
      }

      await tempPreviewUrlRefetch({
        isDefault: selectedItem.is_default,
        rvId: selectedItem.template_revision_id,
      });
    } catch (error) {
      toast.error(
        <Trans i18nKey="toast.error.loading.data.failed">
          Failed to load data.
        </Trans>
      );
    } finally {
      setIsLoaded(false);
    }
  };

  const handleRestore = async () => {
    getTemplateDefaultConfig(templateType, 'Light', 2, customerId).then(r => {
      if (r.pageConfig && r.templateMetadata) {
        updateTemplate(
          token,
          customerId,
          templateId,
          templateType,
          r.templateMetadata,
          '',
          r.pageConfig
        ).then(() => {
          setPublishStatus(!publishStatus);
          setInitValue(_get(r.pageConfig, 'page[0]'));
          toast.success(
            <Trans i18nKey="c.f.simple.button.group.success">success</Trans>
          );
          window.location.reload();
        });
      }
    });
  };

  const handleSelectedTemplate = async (formikProps, targetItem) => {
    formikProps.setFieldValue(
      'is_default',
      _get(targetItem, 'is_default', true)
    );
    formikProps.setFieldValue(
      'template_id',
      _get(targetItem, 'template_id', null)
    );
    formikProps.setFieldValue(
      'template_revision_id',
      _get(targetItem, 'template_revision_id', null)
    );
    setSelectedTempInfo(targetItem);
    updateCustomLang(_get(targetItem, 'langs', []));
    updateCustomDevice(_get(targetItem, 'device_type', []));
    await tempPreviewUrlRefetch({
      isDefault: targetItem.is_default,
      rvId: targetItem.template_revision_id,
    });
  };

  const handleExport = async (templateId, isDirty) => {
    await handleGenericTemplateExport({
      templateId,
      fileName: 'SimulationReportPage_Generic',
      setIsExporting,
      isDirty,
      setModalState,
    });
  };

  const formikConfig = {
    enableReinitialize: true,
    initialValues: initData(initValue),
    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true);

      const {showQRCode, showEmail, showPrint} =
        values.getPhotoPage.accessOptions || {};
      if (!showQRCode && !showEmail && !showPrint) {
        setIsShowAccessOptionsModal(true);
        setSubmitting(false);
        return;
      }

      if (
        !privilegeUtils.canCustomizationAppLayout(auth) &&
        !values.is_default
      ) {
        toast.error(
          <Trans i18nKey="toast.error.customized.version.failed">
            If you need to set up a customized template, please go to Help >
            Contact Us.
          </Trans>
        );
        setSubmitting(false);
        return;
      }

      if (!values.is_default && !values.template_id) {
        toast.error(
          <Trans i18nKey="toast.error.update.template.failed">
            Failed to update, please choose a template.
          </Trans>
        );
        setSubmitting(false);
        return;
      }

      if (!values.is_default) {
        await skincareProDao
          .selectUIConfigTemplate({
            ...selectedTempInfo,
            template_type: templateType,
          })
          .then(r => {
            setIsShowPublishModal(true);
          })
          .catch(r => {
            toast.error(
              <Trans i18nKey="c.f.simple.button.group.failed">failed</Trans>
            );
            setSubmitting(false);
          });
        return;
      }

      //validation
      const invalidImages = treatmentTypes.some(t => {
        if (
          _isEmpty(
            _get(
              values,
              `simulationResultPage.smb.treatmentImages.${t.value}.imageFile`
            )
          )
        ) {
          toast.error(
            'Failed!! Treatment "' +
              Transform(t.label) +
              '" Image cannot be empty.'
          );
          return true;
        }
      });
      const invalidDescription = langUtils.getAppLangList().some(lang =>
        treatmentTypes.some(t => {
          if (
            _isEmpty(
              _get(
                values,
                `simulationResultPage.treatmentDescriptions.${lang.value}.${
                  t.value
                }.treatmentDescription`
              )
            )
          ) {
            toast.error(
              `Failed!! Treatment "` +
                Transform(t.label) +
                `" Description in "${lang.label}" cannot be empty`
            );
            return true;
          }
        })
      );
      if (invalidImages || invalidDescription) {
        setSubmitting(false);
        return;
      }

      let treatmentImages = [];
      await Promise.all(
        treatmentTypes.map(async t =>
          treatmentImages.push({
            file_url: await handleImage(
              _get(
                values,
                `simulationResultPage.smb.treatmentImages.${t.value}.imageFile`
              ),
              token,
              customerId
            ),
            path: !_isEmpty(
              _get(
                values,
                `simulationResultPage.smb.treatmentImages.${t.value}.imageFile`
              )
            )
              ? `./assets/images/img_simulation_report_treatment_${t.value}.png`
              : '',
            type: t.value,
          })
        )
      );
      const metaDataJson = {
        files: [
          {
            file_url: await handleImage(
              _get(
                values,
                'simulationResultPage.reportBanner.mainImage[0].imageFile'
              ),
              token,
              customerId
            ),
            path:
              !_isEmpty(
                _get(
                  values,
                  'simulationResultPage.reportBanner.mainImage[0].imageFile'
                )
              ) && './assets/images/img_simulation_report_banner_phone.png',
          },
          {
            file_url: await handleImage(
              _get(
                values,
                'simulationResultPage.reportBanner.mainImage[1].imageFile'
              ),
              token,
              customerId
            ),
            path:
              !_isEmpty(
                _get(
                  values,
                  'simulationResultPage.reportBanner.mainImage[1].imageFile'
                )
              ) && './assets/images/img_simulation_report_banner_pad.png',
          },
          {
            file_url: await handleImage(
              _get(
                values,
                'simulationResultPage.reportBanner.mainImage[2].imageFile'
              ),
              token,
              customerId
            ),
            path:
              !_isEmpty(
                _get(
                  values,
                  'simulationResultPage.reportBanner.mainImage[2].imageFile'
                )
              ) && './assets/images/img_simulation_report_banner_landscape.jpg',
          },
          ...treatmentImages,
        ],
      };

      const treatmentAllLanguageDesc = await Promise.all(
        langUtils.getAppLangList().map(async l => {
          return {
            [`${l.value}`]: await Promise.all(
              treatmentTypes.map(async t => {
                return {
                  [`${t.value}`]: {
                    treatmentDescription: _get(
                      values.simulationResultPage.treatmentDescriptions,
                      `${l.value}.${t.value}.treatmentDescription`
                    ),
                    idealFor: _get(
                      values.simulationResultPage.treatmentDescriptions,
                      `${l.value}.${t.value}.idealFor`
                    ),
                    treatmentImage: {
                      image: await treatmentImages.find(r => r.type === t.value)
                        .path,
                      imageUrl: await treatmentImages.find(
                        r => r.type === t.value
                      ).file_url,
                    },
                  },
                };
              })
            ),
          };
        })
      );
      const data = {
        page: [
          {
            templateType: 'skincare',
            pageType: 'simulationResult',
            ...values.simulationResultPage,
            smb: {
              treatmentImages: treatmentAllLanguageDesc[0].enu.reduce(
                (data, current) => {
                  data[Object.keys(current)] =
                    current[Object.keys(current)].treatmentImage;
                  return data;
                },
                {}
              ),
            },
            reportBanner: {
              ...values.simulationResultPage.reportBanner,
              mainImage: [
                {
                  type: 'phone',
                  image: metaDataJson.files[0].path,
                  imageUrl: metaDataJson.files[0].file_url,
                },
                {
                  type: 'pad',
                  image: metaDataJson.files[1].path,
                  imageUrl: metaDataJson.files[1].file_url,
                },
                {
                  type: 'padLandscape',
                  image: metaDataJson.files[2].path,
                  imageUrl: metaDataJson.files[2].file_url,
                },
              ],
            },
            treatmentDescriptions: {
              showIdealFor: _get(
                values.simulationResultPage.treatmentDescriptions,
                'showIdealFor'
              ),
              ...treatmentAllLanguageDesc.reduce((data, current) => {
                data[Object.keys(current)] = current[
                  Object.keys(current)
                ].reduce((d, c) => {
                  d[Object.keys(c)] = c[Object.keys(c)];
                  return d;
                }, {});
                return data;
              }, {}),
            },
          },
          {
            templateType: 'skincare',
            pageType: 'simulationGetPhoto',
            ...values.getPhotoPage,
          },
        ],
      };

      await updateTemplate(
        token,
        customerId,
        templateId,
        templateType,
        metaDataJson,
        '',
        data
      )
        .then(async () => {
          if (privilegeUtils.canCustomizationAppLayout(auth)) {
            try {
              await skincareProDao.selectUIConfigTemplate({
                ...selectedTempInfo,
                template_type: templateType,
              });
            } catch (err) {
              toast.error(
                <Trans i18nKey="c.f.simple.button.group.failed">failed</Trans>
              );
            }
          }

          setIsShowPublishModal(true);
        })
        .catch(e => {
          if (isFunctionBusyError(e)) {
            setModalState({
              isShow: true,
              type: 'processingInProgress',
              showFooter: false,
            });
            setSubmitting(false);
            return;
          }

          toast.error(
            <Trans i18nKey="c.f.simple.button.group.failed">failed</Trans>
          );
          setSubmitting(false);
        });
    },
  };

  if (
    !(
      privilegeUtils.canUiConfig(auth) ||
      privilegeUtils.isTechnicalSupport(auth)
    )
  ) {
    history.push('/');
  }

  return isLoaded ? (
    <div>
      <Header />
      <Loader />
    </div>
  ) : (
    <>
      <Formik {...formikConfig} ref={formRef}>
        {formikProps => (
          <Form className="skincare-configuration-form">
            <Header publishStatus={publishStatus} dirty={formikProps.dirty} />
            <div className="skincare-configuration-layout">
              <div className="skincare-configuration-layout__left">
                <div className="kt-portlet skincare-configuration-layout__left-portlet">
                  <div className="kt-portlet__body pb-0">
                    <ChooseUITemplate
                      isDefaultLayout={_get(selectedTempInfo, 'is_default')}
                      setIsDefaultLayout={v => {
                        let targetItem = customTempDataset.find(
                          item => item.is_default === false
                        );
                        if (!targetItem) {
                          targetItem = {
                            is_default: v,
                            template_id: null,
                            template_revision_id: null,
                            device_type: deviceOptions.map(op => op.value),
                            langs: langCode
                              .getConsoleStringLangList()
                              .map(op => op.value),
                          };
                        }
                        if (v) {
                          targetItem = {
                            ...generalTempInfo,
                            is_default: true,
                            device_type: deviceOptions.map(op => op.value),
                            langs: langCode
                              .getConsoleStringLangList()
                              .map(op => op.value),
                          };
                        }
                        handleSelectedTemplate(formikProps, targetItem);
                      }}
                    />
                  </div>
                  <div className="kt-portlet__body skincare-configuration-layout__left-portlet__body">
                    {_get(selectedTempInfo, 'is_default') && (
                      <GenericLayoutSettings
                        formikProps={formikProps}
                        device={device}
                        setDevice={setDevice}
                        treatment={treatment}
                        setTreatment={setTreatment}
                        language={language}
                        setLanguage={setLanguage}
                        tab={tab}
                        setTab={setTab}
                      />
                    )}
                    {!_get(selectedTempInfo, 'is_default') && (
                      <CustomizedLayoutSettings
                        data={customTempDataset}
                        isLoading={isLoaded}
                        selectedTempInfo={selectedTempInfo}
                        handleSelectedTemplate={targetItem =>
                          handleSelectedTemplate(formikProps, targetItem)
                        }
                        tab={tab}
                        setTab={setTab}
                        isTechnicalSupport={isTechnicalSupport}
                      />
                    )}
                  </div>
                  <div className="d-flex justify-content-between kt-margin-t-20">
                    {isTechnicalSupport &&
                    _get(selectedTempInfo, 'is_default') ? (
                      <div className="align-self-center kt-margin-l-10">
                        <ElevatedButton
                          type="custom"
                          faIcon="fa-download"
                          text={
                            <Trans i18nKey="p.s.uiconfiguration.export.current.ui.html.file">
                              Export Current UI As HTML File
                            </Trans>
                          }
                          onClick={() =>
                            handleExport(
                              selectedTempInfo.template_id,
                              formikProps.dirty
                            )
                          }
                          disabled={isExporting}
                        />
                      </div>
                    ) : (
                      <div />
                    )}
                    <div>
                      <ButtonWithRestoreGroup
                        saveDirty={formikProps.dirty}
                        discardButton={false}
                        isUpdating={true}
                        onRestoreClick={() => {
                          handleRestore().then(() => formikProps.resetForm());
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {!isLoadingByTemplateUrl &&
                formikProps.values &&
                treatments.length > 0 && (
                  <div className="skincare-configuration-layout__right">
                    <PreviewModule
                      values={formikProps.values}
                      template={{
                        ...(_get(selectedTempInfo, 'is_default') && {
                          page: {
                            page: [
                              {
                                ...formikProps.values.simulationResultPage,
                                preview: {
                                  treatmentOrder: treatments,
                                  customName: {
                                    custom_simulation_treatment_name: customSimulationTreatmentData,
                                  },
                                },
                              },
                              {
                                ...formikProps.values.getPhotoPage,
                                accessOptions: {
                                  ...formikProps.values.getPhotoPage
                                    .accessOptions,
                                },
                              },
                            ],
                          },
                        }),
                        common: {
                          ...commonData,
                          page: [
                            {
                              ..._get(commonData, 'page[0]'),
                              header: {
                                logo: {
                                  image: _get(
                                    commonData,
                                    'page[0].header.logo.imageUrl',
                                    ''
                                  ),
                                },
                                displayName: _get(
                                  commonData,
                                  'page[0].header.displayName',
                                  'showAll'
                                ),
                              },
                            },
                          ],
                        },
                      }}
                      previewUrl={
                        selectedTempInfo.is_default
                          ? tab === templateType
                            ? _get(templateUrlData, 'indexPage')
                            : _get(templateUrlData, 'getPhotoPage')
                          : tab === templateType
                          ? _get(templateUrlData, 'indexFileUrl')
                          : _get(templateUrlData, 'getPhotoUrl')
                      }
                      lang={language}
                      type="SimulationPage"
                      deviceList={getAvailableDevices(
                        deviceOptions,
                        _get(selectedTempInfo, 'device_type', [])
                      )}
                      langList={langUtils.getAvailableConsoleStringLangList(
                        _get(selectedTempInfo, 'langs', [])
                      )}
                      onChangeDevice={device =>
                        setDevice(setUiConfigDevice(device))
                      }
                      isCustomized={
                        !_get(selectedTempInfo, 'is_default', false)
                      }
                    />
                  </div>
                )}
            </div>
            <PublishToOnlineModal
              isShow={isShowPublishModal}
              onPublish={isPublish => {
                formikProps.setSubmitting(false);
                formikProps.resetForm(formikProps.values);
                setPublishStatus(isPublish);
                setIsShowPublishModal(false);
              }}
            />
            <AccessOptionsHintModal
              show={isShowAccessOptionsModal}
              onHide={() => {
                setIsShowAccessOptionsModal(false);
              }}
            />
            <ConfigurationModal
              modalState={modalState}
              onClose={() => setModalState({isShow: false, type: null})}
            />
          </Form>
        )}
      </Formik>
      {isExporting && (
        <OverLayLoader
          text={
            <Trans i18nKey="c.spinner.overlay.loader.exporting">
              Exporting...
            </Trans>
          }
        />
      )}
    </>
  );
}

const initData = values => {
  if (_isEmpty(values)) return;
  const simulationResultPage = _get(values, 'page[0]', {});
  const simulationGetPhotoPage = _get(values, 'page[1]', {});
  const {page, ...restV} = values;
  return {
    ...restV,
    simulationResultPage: {
      ...simulationResultPage,
      reportBanner: {
        ...simulationResultPage.reportBanner,
        mainImage: [
          {
            ...simulationResultPage.reportBanner.mainImage[0],
            imageFile: simulationResultPage.reportBanner.mainImage[0].imageUrl,
          },
          {
            ...simulationResultPage.reportBanner.mainImage[1],
            imageFile: simulationResultPage.reportBanner.mainImage[1].imageUrl,
          },
          _get(simulationResultPage, 'reportBanner.mainImage[2]')
            ? {
                ..._get(simulationResultPage, 'reportBanner.mainImage[2]'),
                imageFile: _get(
                  simulationResultPage,
                  'reportBanner.mainImage[2].imageUrl'
                ),
              }
            : {
                type: 'padLandscape',
                image:
                  './assets/images/img_simulation_report_banner_landscape.jpg',
                imageUrl:
                  'https://app-cdn-01.perfectcorp.com/console-demo/common/misc/template_default_config/image/img_simulation_report_banner_landscape.jpg',
              },
        ],
      },
      smb: {
        treatmentImages: {
          ...treatmentTypes.reduce((data, current) => {
            data[current.value] = {
              image: _get(
                simulationResultPage,
                `smb.treatmentImages.${current.value}.image`
              ),
              imageUrl: _get(
                simulationResultPage,
                `smb.treatmentImages.${current.value}.imageUrl`
              ),
              imageFile: _get(
                simulationResultPage,
                `smb.treatmentImages.${current.value}.imageUrl`
              ),
            };
            return data;
          }, {}),
        },
      },
    },
    getPhotoPage: {
      ...simulationGetPhotoPage,
      accessOptions: {
        showQRCode: _get(
          simulationGetPhotoPage,
          'accessOptions.showQRCode',
          true
        ),
        showEmail: _get(
          simulationGetPhotoPage,
          'accessOptions.showEmail',
          true
        ),
        showPrint: _get(
          simulationGetPhotoPage,
          'accessOptions.showPrint',
          true
        ),
      },
    },
  };
};
